import { useMemo, useCallback } from 'react';
import {
  allJeansIds,
  allBrasIds,
  allShirtsIds,
  allClothingIds,
  allShoesIds,
  allRingsIds,
  allBeltsIds,
  allGlovesIds,
  allHatsIds,
  categoriesWithSizes,
  categoriesWithSleeveLengths,
  categoriesWithHeelHeights,
  categoriesWithToeTypes,
  categoriesWithToeShapes,
  shoesFiltersExclusion,
  highHeeledFiltersExclusion,
  toeTypeShapeFiltersExclusion,
  allEyewear,
} from './categoriesIds';
import { Category } from '../../models/category';

// const womenCategoryId = '1';
// const menCategoryId = '124';

export const useCategoryWithSizes = ({ categoryId, categories }: { categories?: Category[]; categoryId: string }) => {
  const getDefaultSizeGuideTabId = useCallback(currentCategoryId => {
    if (allJeansIds.includes(currentCategoryId)) {
      return ['tab_jeans'];
    }
    if (allBrasIds.includes(currentCategoryId)) {
      return ['tab_bras'];
    }
    if (allShirtsIds.includes(currentCategoryId)) {
      return ['tab_shirts'];
    }

    if (allClothingIds.includes(currentCategoryId)) {
      return ['tab_clothing'];
    }

    if (allShoesIds.includes(currentCategoryId)) {
      return ['tab_shoes'];
    }

    if (allRingsIds.includes(currentCategoryId)) {
      return ['tab_rings', 'tab_accessories'];
    }
    if (allBeltsIds.includes(currentCategoryId)) {
      return ['tab_belts', 'tab_accessories'];
    }
    if (allGlovesIds.includes(currentCategoryId)) {
      return ['tab_gloves', 'tab_accessories'];
    }
    if (allHatsIds.includes(currentCategoryId)) {
      return ['tab_hats', 'tab_accessories'];
    }

    return ['tab_clothing'];
  }, []);

  return useMemo(() => {
    const isShoesCategory = allShoesIds.indexOf(categoryId) > -1;
    const defaultSizeTab = getDefaultSizeGuideTabId(categoryId);

    if (!categories) {
      return {
        categoryWithSizes: null,
        defaultSizeTab,
        isShoesCategory,
      };
    }

    const found = categories.find(c => c.id === categoryId);
    if (categoriesWithSizes.indexOf(categoryId) > -1 || categoriesWithSizes.indexOf(found?.parentId) > -1) {
      return {
        categoryWithSizes: found,
        defaultSizeTab,
        isShoesCategory,
      };
    }

    return {
      categoryWithSizes: null,
      defaultSizeTab,
      isShoesCategory,
    };
  }, [categories, categoryId, getDefaultSizeGuideTabId]);
};

const hasPropertyTransitive = (
  catId: string,
  catsById: Map<string, Category>,
  predicate: (catId: string) => boolean,
): boolean => {
  if (predicate(catId)) {
    return true;
  }
  const cat = catsById.get(catId);
  return cat && cat.parentId && hasPropertyTransitive(cat.parentId, catsById, predicate);
};

const useGetCategoriesById = (categories: Category[]): Map<string, Category> =>
  useMemo(() => categories.reduce((acc, cat) => acc.set(cat.id, cat), new Map<string, Category>()), [categories]);

export const useIsCategoryWithSleeveLengths = (categories: Category[], categoryId: string): boolean => {
  const catsById = useGetCategoriesById(categories);
  return useMemo(() => {
    const hasSleeveLengths = (catId: string) => categoriesWithSleeveLengths.indexOf(catId) > -1;
    return hasPropertyTransitive(categoryId, catsById, hasSleeveLengths);
  }, [categoryId, catsById]);
};

export const useIsCategoryWithHeelHeights = (categories: Category[], categoryId: string): boolean => {
  const catsById = useGetCategoriesById(categories);
  return useMemo(() => {
    if (shoesFiltersExclusion.indexOf(categoryId) > -1 || highHeeledFiltersExclusion.indexOf(categoryId) > -1) {
      return false;
    }
    const hasHeelHeights = (catId: string) => categoriesWithHeelHeights.indexOf(catId) > -1;
    return hasPropertyTransitive(categoryId, catsById, hasHeelHeights);
  }, [categoryId, catsById]);
};

export const useIsCategoryWithToeTypes = (categories: Category[], categoryId: string): boolean => {
  const catsById = useGetCategoriesById(categories);
  return useMemo(() => {
    if (shoesFiltersExclusion.indexOf(categoryId) > -1 || toeTypeShapeFiltersExclusion.indexOf(categoryId) > -1) {
      return false;
    }
    const hasToeTypes = (catId: string) => categoriesWithToeTypes.indexOf(catId) > -1;
    return hasPropertyTransitive(categoryId, catsById, hasToeTypes);
  }, [categoryId, catsById]);
};

export const useIsCategoryWithToeShapes = (categories: Category[], categoryId: string): boolean => {
  const catsById = useGetCategoriesById(categories);
  return useMemo(() => {
    if (shoesFiltersExclusion.indexOf(categoryId) > -1 || toeTypeShapeFiltersExclusion.indexOf(categoryId) > -1) {
      return false;
    }
    const hasToeShapes = (catId: string) => categoriesWithToeShapes.indexOf(catId) > -1;
    return hasPropertyTransitive(categoryId, catsById, hasToeShapes);
  }, [categoryId, catsById]);
};

export const useIsCategoryWithSizes = (categories: Category[], categoryId: string): boolean => {
  const catsById = useGetCategoriesById(categories);
  return useMemo(() => {
    const hasToeShapes = (catId: string) => categoriesWithSizes.indexOf(catId) > -1;
    return hasPropertyTransitive(categoryId, catsById, hasToeShapes);
  }, [categoryId, catsById]);
};

export const useIsCategoryEyewear = (categoryId: string): boolean => {
  return useMemo(() => {
    return allEyewear.indexOf(categoryId) > -1;
  }, [categoryId]);
};

export const useIsCategoryHome = (category: Category): boolean => {
  return useMemo(() => {
    return category?.department.internalName === 'home';
  }, [category]);
};
